import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Brain, Check, X, AlarmClock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Paywall from "@/components/Paywall";
import BundleHint from "@/components/BundleHint";
import { useI18n } from "@/context/I18nContext";
import { qTypeLabel } from "@/lib/practiceLabels";

export default function Quiz() {
  const { t } = useI18n();
  const [due, setDue] = useState(0);
  const [questions, setQuestions] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [fillValue, setFillValue] = useState("");
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState("");
  const [paywall, setPaywall] = useState(false);

  // Mount-only: load due count once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/quiz/due").then((r) => setDue(r.data.due_count)).catch((e) => console.warn("due load failed", e)); }, []);

  const generate = async (opts) => {
    setLoading(true);
    setQuestions(null); setDone(null); setIdx(0); setAnswers([]);
    try {
      const res = await api.post("/quiz/generate", opts);
      if (!res.data.questions?.length) { toast.error(t("pm.quiz.noQuestions")); return; }
      setQuestions(res.data.questions);
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(true);
      else toast.error(e?.response?.data?.detail || t("pm.quiz.buildFailed"));
    } finally { setLoading(false); }
  };

  const q = questions?.[idx];
  const answer = (val) => {
    const correct = val?.trim().toLowerCase() === q.answer?.trim().toLowerCase();
    const newAnswers = [...answers, { word: q.word, correct }];
    setAnswers(newAnswers);
    setSelected(val);
    setTimeout(async () => {
      setSelected(null); setFillValue("");
      if (idx + 1 < questions.length) setIdx(idx + 1);
      else {
        try {
          const res = await api.post("/quiz/grade", { results: newAnswers });
          setDone(res.data);
          api.get("/quiz/due").then((r) => setDue(r.data.due_count)).catch(() => {});
        } catch { toast.error(t("pm.quiz.gradeFailed")); }
      }
    }, 700);
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl">QuizForge</h1>
        <p className="text-gray-600">{t("pm.quiz.subtitle")}</p>
        <BundleHint id="quiz">{t("pm.quiz.hint")}</BundleHint>

        {!questions && !done && (
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <button onClick={() => generate({ due_only: true, count: 8 })} disabled={loading} className="nb-card bg-coral text-white p-6 text-start hover:-translate-y-1 transition-transform" data-testid="quiz-review-due">
              <AlarmClock className="w-8 h-8" />
              <div className="font-cabinet font-extrabold text-4xl mt-2">{due}</div>
              <div className="font-bold">{t("pm.quiz.wordsDue", { count: due })}</div>
            </button>
            <div className="nb-card bg-mint p-6">
              <Sparkles className="w-8 h-8" />
              <div className="font-cabinet font-bold text-lg mt-2">{t("pm.quiz.topicTitle")}</div>
              <input className="nb-input mt-2" placeholder={t("pm.quiz.topicPlaceholder")} value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="quiz-topic-input" />
              <button onClick={() => generate({ topic, count: 6 })} disabled={loading || !topic} className="nb-btn-primary w-full mt-3 py-2" data-testid="quiz-generate-btn">{t("pm.quiz.generate")}</button>
            </div>
          </div>
        )}

        {loading && <div className="text-center py-16"><div className="w-14 h-14 border-4 border-ink border-t-coral rounded-full animate-spin mx-auto" /><p className="font-cabinet font-bold mt-3">{t("pm.quiz.building")}</p></div>}

        {q && !done && (
          <motion.div key={idx} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="nb-card p-6 mt-6" data-testid="quiz-question">
            <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-gray-500">
              <span>{t("pm.quiz.question", { n: idx + 1, total: questions.length })}</span><span>{qTypeLabel(t, q.type)}</span>
            </div>
            <p className="font-cabinet font-bold text-2xl mt-3" dir="auto">{q.question}</p>
            {q.type === "mcq" ? (
              <div className="grid gap-3 mt-5">
                {q.options?.map((o) => {
                  const isAns = selected && o === q.answer;
                  const isWrong = selected === o && o !== q.answer;
                  return (
                    <button key={o} disabled={!!selected} onClick={() => answer(o)} data-testid="quiz-option"
                      className={`nb-btn-outline justify-start py-3 px-4 ${isAns ? "bg-mint" : isWrong ? "bg-red-200" : ""}`}>
                      {isAns && <Check className="w-5 h-5" />}{isWrong && <X className="w-5 h-5" />} <span dir="auto">{o}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex gap-2 mt-5">
                <input className="nb-input flex-1" value={fillValue} onChange={(e) => setFillValue(e.target.value)} placeholder={t("pm.quiz.answerPlaceholder")} dir="auto" disabled={!!selected} onKeyDown={(e) => e.key === "Enter" && answer(fillValue)} data-testid="quiz-fill-input" />
                <button onClick={() => answer(fillValue)} disabled={!!selected} className="nb-btn-primary px-5" data-testid="quiz-check-btn">{t("pm.quiz.check")}</button>
              </div>
            )}
          </motion.div>
        )}

        {done && (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="nb-card bg-butter p-8 mt-6 text-center" data-testid="quiz-result">
            <div className="text-5xl">{done.correct === done.total ? "🏆" : "💪"}</div>
            <h2 className="font-cabinet font-extrabold text-3xl mt-3"><bdi>{t("pm.quiz.correct", { correct: done.correct, total: done.total })}</bdi></h2>
            <p className="font-bold" data-testid="quiz-xp">{t("pm.quiz.xpEarned", { count: done.xp_earned })}</p>
            <button onClick={() => { setQuestions(null); setDone(null); }} className="nb-btn-primary mt-5 px-8 py-3" data-testid="quiz-back-btn">{t("pm.quiz.back")}</button>
          </motion.div>
        )}
      </div>
      <Paywall open={paywall} onClose={() => setPaywall(false)} reason={t("pm.common.proFeature", { name: "QuizForge" })} />
    </AppLayout>
  );
}
